import { GrowthCategory } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import {
  EventTypes,
  EventTypes as CoreEventTypes,
  PageSource,
  track,
  trackGoldStandardEvent,
} from '@mc/track-event'
import {
  ActionTypes,
  KeyTypes,
  saveUserData,
  getTrackedId,
} from '@mc/user-data'
import { setMetaProductIdCookie } from 'libs/cookies/metaProductIdCookie'
import { SubcategoryWithOutcome } from 'types/data'

export const useSubcategoryPageEvents = () => {
  const { subcatToOutcomes, primaryCategory } = useStaticData<{
    subcatToOutcomes: SubcategoryWithOutcome
    primaryCategory: GrowthCategory
  }>()

  const getPageCategoryInfo = () => ({
    categoryId: primaryCategory.id,
    categoryName: primaryCategory.name,
    subcategoryId: subcatToOutcomes.id,
    subcategoryName: subcatToOutcomes.name,
  })

  const trackSubcategoryPageEvent = (
    eventName: string,
    eventProps: Record<string, string | number | undefined>,
  ) => {
    track(eventName, {
      ...eventProps,
      ...getPageCategoryInfo(),
      page_source: PageSource.SUBCATEGORY,
    })
  }

  const trackProductListViewed = (categoryId: number) => {
    const catId = getTrackedId({ key: KeyTypes.CATEGORY, id: categoryId })
    const subcatId = getTrackedId({
      key: KeyTypes.SUBCATEGORY,
      id: getPageCategoryInfo().subcategoryId,
    })

    const eventDetails = {
      products: [{ product_id: subcatId }],
      category_ids: [catId],
      stored_category_id: catId,
      stored_subcategory_id: subcatId,
    }

    trackGoldStandardEvent(
      CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
      eventDetails,
    )

    saveUserData({
      key: KeyTypes.CATEGORY,
      id: catId,
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })

    saveUserData({
      key: KeyTypes.SUBCATEGORY,
      id: subcatId,
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })

    setMetaProductIdCookie(subcatId)
  }

  const trackBreadcrumbClicked = (categoryName: string) => {
    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_CLICK, {
      action: categoryName,
      location: 'breadcrumbs',
      category: 'category',
    })
  }

  const trackOutcomeSelectionTabClicked = ({
    outcomeName,
    outcomeId,
    tilePosition,
    tileCount,
  }: {
    outcomeName: string
    outcomeId?: number
    tilePosition?: number
    tileCount?: number
  }) => {
    const key = KeyTypes.OUTCOME
    const parsedOutcomeId = outcomeId
      ? getTrackedId({ id: outcomeId, key })
      : ''
    const eventName = EventTypes.SUBCATEGORY_PAGE_CLICK

    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_CLICK, {
      action: outcomeName,
      location: 'outcome_selection_tabs',
      category: 'outcome_tiles',
      tile_position_index: tilePosition,
      total_number_of_tiles: tileCount,
      stored_outcome_id: parsedOutcomeId,
    })

    saveUserData({
      key,
      id: parsedOutcomeId,
      actionType: ActionTypes.BROWSING,
      eventName,
    })
  }

  const trackOutcomeCarouselViewed = (
    outcomeName: string,
    rowIndex: number,
    rowCount: number,
  ) => {
    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_ROW_VIEWED, {
      row_name: outcomeName,
      row_position_index: rowIndex + 1,
      total_number_of_rows: rowCount,
      outcomeName,
    })
  }

  const trackOutcomeTileClicked = (
    action: string,
    outcomeName: string,
    category: string,
    tileClass: string,
    location: string,
    position: {
      rowIndex?: number
      rowCount?: number
      colIndex: number
      colCount: number
    },
  ) => {
    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_CLICK, {
      action,
      category,
      class: tileClass,
      location,
      row_position_index: position.rowIndex,
      total_number_of_rows: position.rowCount,
      tile_position_index: position.colIndex,
      total_number_of_tiles: position.colCount,
      outcomeName,
    })
  }

  const trackComponentViewed = (
    location: string,
    rowPositionIndex: number,
    outcomeName?: string,
  ) => {
    track(EventTypes.SUBCATEGORY_PAGE_ROW_VIEWED, {
      row_name: location,
      row_position_index: rowPositionIndex,
      outcomeName,
      location,
      ...getPageCategoryInfo(),
      page_source: PageSource.SUBCATEGORY,
    })
  }

  const trackInstructorTileClicked = (colIdx: number, tileClass: string) => {
    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_CLICK, {
      location: 'meet_instructors',
      class: tileClass,
      position_index: colIdx,
    })
  }

  const trackArticleTileClicked = (colIdx: number, articleSlug: string) => {
    trackSubcategoryPageEvent(EventTypes.SUBCATEGORY_PAGE_CLICK, {
      location: 'related_articles',
      class: articleSlug,
      position_index: colIdx,
    })
  }

  return {
    getPageCategoryInfo,
    trackProductListViewed,
    trackBreadcrumbClicked,
    trackOutcomeSelectionTabClicked,
    trackOutcomeCarouselViewed,
    trackOutcomeTileClicked,
    trackComponentViewed,
    trackInstructorTileClicked,
    trackArticleTileClicked,
  }
}
